import React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  badgeHidesPublicUnlockStats,
  getAllBadges,
  getRoleBadge,
} from './badgeDefinitions';
import { BadgeGraphic } from './BadgeGraphics';
import {
  fetchBadgeUnlockCounts,
  formatUnlockPercent,
} from '../../utils/badgeUnlockCountsService';
import './BadgeList.css';

const BADGE_FLYOUT_ICON_PX = 52;
const BADGE_FLYOUT_VIEWPORT_TOP_MIN = 88;
const BADGE_RARITY_PANEL_CLASS = new Set([
  'common',
  'uncommon',
  'rare',
  'epic',
  'legendary',
]);

function useBadgeUnlockCountsState() {
  const [unlockData, setUnlockData] = React.useState({
    totalUsers: 0,
    badges: {},
  });

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      const next = await fetchBadgeUnlockCounts();
      if (!cancelled) setUnlockData(next);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return unlockData;
}

function useBadgeFlyout(unlockData) {
  const [flyout, setFlyout] = React.useState(null);

  const showFlyout = React.useCallback((e, payload) => {
    const el = e.currentTarget;
    const r = el.getBoundingClientRect();
    const x = r.left + r.width / 2;
    const preferAbove = r.top > BADGE_FLYOUT_VIEWPORT_TOP_MIN;
    setFlyout({
      badgeId: payload.badgeId,
      rarity: payload.rarity,
      x,
      preferAbove,
      anchorTop: r.top,
      anchorBottom: r.bottom,
    });
  }, []);

  const hideFlyout = React.useCallback(() => {
    setFlyout(null);
  }, []);

  const flyoutNode =
    flyout &&
    typeof document !== 'undefined' &&
    createPortal(
      <BadgeFlyoutPanel flyout={flyout} unlockData={unlockData} />,
      document.body
    );

  return { showFlyout, hideFlyout, flyoutNode };
}

function formatPercentForLocale(pct, locale) {
  if (pct == null || !Number.isFinite(pct)) return null;
  try {
    return new Intl.NumberFormat(locale || undefined, {
      maximumFractionDigits: 1,
      minimumFractionDigits: 0,
    }).format(pct);
  } catch {
    return String(Math.round(pct * 10) / 10);
  }
}

function BadgeFlyoutPanel({ flyout, unlockData }) {
  const { t, i18n } = useTranslation(['profile']);
  const hidePopulation = badgeHidesPublicUnlockStats(flyout.badgeId);
  const count = unlockData.badges[flyout.badgeId];
  const total = unlockData.totalUsers;
  const unlockCount = typeof count === 'number' ? count : 0;
  const pct = formatUnlockPercent(unlockCount, total);
  const pctFormatted = formatPercentForLocale(pct, i18n.language);
  const tierLabel =
    flyout.rarity && typeof flyout.rarity === 'string'
      ? t(`badges.rarity.${flyout.rarity}`, flyout.rarity)
      : null;
  const showPercent =
    !hidePopulation && total > 0 && pctFormatted != null;
  const showCountsUnavailable = !hidePopulation && !showPercent;
  const rarityPanelClass =
    flyout.rarity && BADGE_RARITY_PANEL_CLASS.has(flyout.rarity)
      ? `badge-hover-flyout-panel--${flyout.rarity}`
      : '';

  return (
    <div
      className="badge-hover-flyout"
      aria-hidden
      style={{
        position: 'fixed',
        left: flyout.x,
        top: flyout.preferAbove ? flyout.anchorTop : flyout.anchorBottom + 8,
        transform: flyout.preferAbove
          ? 'translate(-50%, calc(-100% - 8px))'
          : 'translate(-50%, 0)',
        zIndex: 10050,
      }}
    >
      <div
        className={['badge-hover-flyout-panel', rarityPanelClass]
          .filter(Boolean)
          .join(' ')}
      >
        <div className="badge-hover-flyout-graphic-wrap">
          <BadgeGraphic badgeId={flyout.badgeId} size={BADGE_FLYOUT_ICON_PX} />
        </div>
        <div className="badge-hover-flyout-meta">
          {tierLabel ? (
            <p className="badge-hover-flyout-line">
              <span className="badge-hover-flyout-k">{t('badgeFlyout.rarityLabel')}</span>
              <span className="badge-hover-flyout-v">{tierLabel}</span>
            </p>
          ) : null}
          {showPercent ? (
            <p
              className={`badge-hover-flyout-line badge-hover-flyout-line--pct${
                tierLabel ? '' : ' badge-hover-flyout-line--pct-only'
              }`}
            >
              <span className="badge-hover-flyout-v badge-hover-flyout-v--emph">
                {t('badgeFlyout.percentLine', { pct: pctFormatted })}
              </span>
            </p>
          ) : null}
          {showCountsUnavailable ? (
            <p className="badge-hover-flyout-line badge-hover-flyout-line--muted">
              {t('achievementsPopup.countsUnavailable')}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/**
 * Komponent wyświetlający listę odznak użytkownika
 */
function BadgeList({ 
  userRole, 
  stats, 
  showRoleBadge = true,
  showActivityBadges = true,
  size = 'medium', // 'small' | 'medium' | 'large'
  maxBadges = null, // null = wszystkie
  className = ''
}) {
  const { t } = useTranslation(['profile']);
  const unlockData = useBadgeUnlockCountsState();
  const { showFlyout, hideFlyout, flyoutNode } = useBadgeFlyout(unlockData);

  const badges = getAllBadges(userRole, stats, {
    includeRoleBadge: showRoleBadge,
    includeActivityBadges: showActivityBadges
  });

  if (badges.length === 0) {
    return null;
  }

  const displayBadges = maxBadges ? badges.slice(0, maxBadges) : badges;
  const remainingCount = maxBadges && badges.length > maxBadges ? badges.length - maxBadges : 0;
  const badgeIconPx = size === 'small' ? 20 : size === 'large' ? 30 : 25;

  return (
    <div className={`badge-list badge-list--${size} ${className}`}>
      {flyoutNode}
      {displayBadges.map(badge => (
        <div 
          key={badge.id} 
          className="badge-item"
          onMouseEnter={e =>
            showFlyout(e, { badgeId: badge.id, rarity: badge.rarity })
          }
          onMouseLeave={hideFlyout}
          style={{
            '--badge-color': badge.color,
            '--badge-bg': badge.bgColor,
            '--badge-border': badge.borderColor
          }}
          title={
            badge.descriptionKey
              ? `${t(badge.labelKey, badge.label)}. ${t(badge.descriptionKey)}`
              : t(badge.labelKey, badge.label)
          }
        >
          <span className="badge-icon">
            <BadgeGraphic badgeId={badge.id} size={badgeIconPx} />
          </span>
          {size !== 'small' && (
            <span className="badge-label">{t(badge.labelKey, badge.label)}</span>
          )}
        </div>
      ))}
      {remainingCount > 0 && (
        <div className="badge-item badge-more">
          <span className="badge-label">+{remainingCount}</span>
        </div>
      )}
    </div>
  );
}

/**
 * Komponent wyświetlający pojedynczą odznakę roli
 */
export function RoleBadge({ userRole, size = 'small', className = '' }) {
  const { t } = useTranslation(['profile']);
  const unlockData = useBadgeUnlockCountsState();
  const { showFlyout, hideFlyout, flyoutNode } = useBadgeFlyout(unlockData);

  // Nie pokazuj odznaki dla zwykłego usera
  if (!userRole || userRole === 'user') {
    return null;
  }

  const badge = getRoleBadge(userRole);
  if (!badge) return null;

  const roleIconPx =
    size === 'tiny' ? 12 : size === 'small' ? 13 : size === 'medium' ? 15 : size === 'large' ? 18 : 14;

  const roleTitle = badge.descriptionKey
    ? `${t(badge.labelKey, badge.label)}. ${t(badge.descriptionKey)}`
    : t(badge.labelKey, badge.label);

  return (
    <span 
      className={`role-badge role-badge--${size} ${className}`}
      onMouseEnter={e =>
        showFlyout(e, { badgeId: badge.id, rarity: badge.rarity })
      }
      onMouseLeave={hideFlyout}
      style={{
        '--badge-color': badge.color,
        '--badge-bg': badge.bgColor,
        '--badge-border': badge.borderColor
      }}
      title={roleTitle}
    >
      <span className="role-badge-icon">
        <BadgeGraphic badgeId={badge.id} size={roleIconPx} />
      </span>
      {size !== 'tiny' && (
        <span className="role-badge-label">{t(badge.labelKey, badge.label)}</span>
      )}
      {flyoutNode}
    </span>
  );
}

export default BadgeList;
